import { Dialog } from 'primereact/dialog';
import { FC, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedTeacherDashboardFilter } from '../../redux/reducers/teacherDashboardFiltersSlice';
import { DailySnapshotQueryFilters } from './DailySnapshot';
import { DashboardStudentsListDailyQuery } from '../../graphQLOperations/__generated__/DashboardStudentsListDailyQuery.graphql';
import { useLazyLoadQuery } from 'react-relay';
import { getDailyStudentDetails, getWeeklyStudentDetail } from '../../graphQLOperations/Dashboard';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { WeeklySnapshotQueryFilters } from './WeeklySnapshot';
import { endOfWeek, startOfWeek } from 'date-fns';
import { DashboardStudentsListWeeklyQuery } from '../../graphQLOperations/__generated__/DashboardStudentsListWeeklyQuery.graphql';
import CopyListOfStudentsButton from './CopyListOfStudentsButton';
import { Skill } from '../../models/Skill';
import { sortByFirstNameThenLastName } from '../../businessLogicService/sortCriteria';
import { selectUser } from '../../redux/reducers/userSlice';
import { getDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { Button } from 'primereact/button';
import { Roles } from '../../models/User';
import { FilterOptionValues } from './TeacherDashboard';
import { StudentNameLink } from '../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { MaskedInput } from '../MaskedData/MaskedInput';
import { InputText } from 'primereact/inputtext';

interface Props {
  visible: boolean;
  setModalVisible: (v: boolean) => void;
  header: string;
  label: string;
  score: number | null | undefined;
  type: 'week' | 'day';
  filterOption: FilterOptionValues;
}
interface StudentsRaw {
  id: string;
  firstName: string;
  lastName: string;
  skills?: Skill[];
  publicUserId: string;
}

const filtersPrime = {
  label: { value: null, matchMode: FilterMatchMode.CONTAINS },
};

export const StudentsModal: FC<Props> = ({ setModalVisible, visible, header, label, score, type, filterOption }) => {
  const toast = useRef<Toast>(null);
  type StudentRow = { id: string; label: string; skills: string[]; publicUserId: string };
  const [studentList, setStudentList] = useState<StudentRow[]>([]);
  const [studentListRaw, setStudentListRaw] = useState<StudentsRaw[]>([]);
  const userRedux = useAppSelector(selectUser);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const isSiteAdmin = userRedux?.roles.some(role => role.name === Roles.SITE_ADMIN);
  const filters: DailySnapshotQueryFilters = {
    targetGroupIds:
      filterOption === FilterOptionValues.ROADMAP && isSiteAdmin && teacherDashboardFilters.roadmaps
        ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
        : null,
    classIds:
      (filterOption === FilterOptionValues.CLASSES || !isSiteAdmin) && teacherDashboardFilters.classes?.length
        ? teacherDashboardFilters.classes.map(cl => cl.id)
        : null,
    siteIds:
      isSiteAdmin && teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
    gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades.map(cl => cl.id) : null,
    reportDate: getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate,
  };
  const filtersWeekly: WeeklySnapshotQueryFilters = {
    targetGroupIds:
      filterOption === FilterOptionValues.ROADMAP && isSiteAdmin && teacherDashboardFilters.roadmaps
        ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
        : null,
    classIds:
      (filterOption === FilterOptionValues.CLASSES || !isSiteAdmin) && teacherDashboardFilters.classes?.length
        ? teacherDashboardFilters.classes.map(cl => cl.id)
        : null,
    siteIds:
      isSiteAdmin && teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
    gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades.map(cl => cl.id) : null,
    reportFromDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    reportToDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
  };

  const daily = useLazyLoadQuery<DashboardStudentsListDailyQuery>(
    getDailyStudentDetails,
    { filter: filters, score, skip: score === undefined || type !== 'day' },
    { fetchPolicy: 'network-only', fetchKey: type },
  );
  const weekly = useLazyLoadQuery<DashboardStudentsListWeeklyQuery>(
    getWeeklyStudentDetail,
    { filter: filtersWeekly, passed: score, skip: score === undefined || type !== 'week' },
    { fetchPolicy: 'network-only', fetchKey: type },
  );

  useEffect(() => {
    let valuesRaw: StudentsRaw[] = [];
    if (type === 'day') {
      valuesRaw =
        daily.studentsSkillAssessmentsDailySnapshotDetail?.map(item => {
          return {
            id: (item.student?.id as string) ?? '',
            firstName: item.student?.firstName as string,
            lastName: item.student?.lastName as string,
            skills: item.skills as Skill[],
            publicUserId: item.student?.publicUserId ?? '',
          };
        }) || [];
    } else if (type === 'week') {
      valuesRaw =
        weekly.studentsSkillAssessmentsWeeklySnapshotDetail?.map(item => {
          return {
            id: (item.student?.id as string) ?? '',
            firstName: item.student?.firstName as string,
            lastName: item.student?.lastName as string,
            skills: item.skills as Skill[],
            publicUserId: item.student?.publicUserId ?? '',
          };
        }) || [];
    }
    setStudentList(
      valuesRaw
        .sort(
          (a, b) =>
            (a.lastName || '').localeCompare(b.lastName || '', undefined, { sensitivity: 'base' }) ||
            (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' }),
        )
        .map(el => ({
          id: el.id,
          label: `${el.firstName} ${el.lastName}`,
          skills: el.skills?.length ? el.skills?.map(skill => `${skill.name} (${skill.code})`) : [],
          publicUserId: el.publicUserId,
        })),
    );
    setStudentListRaw([...valuesRaw].sort(sortByFirstNameThenLastName));
  }, [daily, weekly]);

  const skillsBodyTemplate = (rowData: { label: string; skills: string[] }) => {
    return (
      <div>
        {rowData.skills.length ? (
          rowData.skills.map(skill => <p key={skill}>{skill}</p>)
        ) : (
          <p>{type === 'week' ? 'No skills passed to display' : '-'}</p>
        )}
      </div>
    );
  };

  const nameBodyTemplate = (rowData: StudentRow) => (
    <StudentNameLink studentId={rowData.id} classIds={teacherDashboardFilters?.classes?.map(c => c.id)}>
      <MaskedStudentData type={PiiTypes.FullName} student={rowData}>
        {rowData.label}
      </MaskedStudentData>
    </StudentNameLink>
  );

  return (
    <Dialog
      className="lg:w-6"
      header={header}
      visible={visible}
      pt={{
        content: {
          style: { paddingBottom: '0px' },
        },
      }}
      onHide={() => {
        setModalVisible(false);
      }}
      breakpoints={{ '880px': '80vw' }}
      footer={<Button label="Close" outlined className="tw-ml-auto" onClick={() => setModalVisible(false)}></Button>}
    >
      <div>
        <div className="tw-flex tw-justify-between tw-items-center">
          {type === 'week' && (
            <>
              <h2 className="tw-font-semibold md:tw-text-base tw-text-xs" style={{ fontStyle: 'normal' }}>
                {`${
                  score === null
                    ? 'No skills attempted'
                    : `${score === 0 ? label + ` New` : label} Skill${score === 1 ? '' : 's'} passed for the week`
                }`}
                {' - '} {`${studentList.length} Student${studentList.length === 1 ? '' : 's'}`}
              </h2>
            </>
          )}
          {type === 'day' && (
            <h2 className="tw-font-semibold md:tw-text-base tw-text-xs" style={{ fontStyle: 'normal' }}>
              {`${score === null ? 'No skills attempted' : label}`} {' - '}
              {`${studentList.length} Student${studentList.length === 1 ? '' : 's'}`}
            </h2>
          )}
          <CopyListOfStudentsButton studentList={studentListRaw} />
        </div>

        {type === 'week' && score === null && (
          <p className="tw-font-atkinson tw-text-base tw-text-neutral-950 tw-m-0">
            These students have not attempted any Skill Challenges this week.
          </p>
        )}

        {type === 'week' && score === 0 && (
          <p className="tw-font-atkinson tw-text-base tw-text-neutral-950 tw-m-0">
            These students attempted skill challenges, but no new skills were passed this week—either challenges were
            unsuccessful or students practiced previously passed skills. See the{' '}
            <span
              className="tw-italic tw-cursor-pointer tw-underline tw-text-blue-500 hover:tw-text-blue-700"
              onClick={() => {
                setModalVisible(false);
                requestAnimationFrame(() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: 'smooth',
                  });
                });
              }}
            >
              Skill Challenges attempted per student{' '}
            </span>
            widget for more details.
          </p>
        )}

        <div className="tw-mt-3">
          <DataTable
            filterDisplay="row"
            filters={filtersPrime}
            value={studentList}
            rows={5}
            rowsPerPageOptions={[5, 10, 25]}
            stripedRows
            tableStyle={{ minHeight: '36px' }}
            paginator
            emptyMessage="No students found."
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
          >
            <Column
              showFilterMenu={false}
              className="w-5 p-1"
              filter
              header="Name"
              field="label"
              body={nameBodyTemplate}
              filterElement={({ index, value, filterApplyCallback }) => (
                <MaskedInput>
                  <InputText
                    value={value || ''}
                    onChange={e => filterApplyCallback(e.target.value, index)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        filterApplyCallback(e.currentTarget.value, index);
                      }
                    }}
                    className="p-column-filter"
                  />
                </MaskedInput>
              )}
            />
            {(type === 'week' && !score) || (type === 'week' && score === 0) ? (
              <></>
            ) : (
              <Column header="Skills" className="w-7 p-1" body={skillsBodyTemplate} />
            )}
          </DataTable>
        </div>
      </div>
      <Toast ref={toast} />
    </Dialog>
  );
};
